import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { useSelection } from '@spazious/dollhouse-engine';
import { FNode } from '@spazious/dollhouse-fsetup';
import { useStaging } from '@spazious/dollhouse-store';
import { hasOwnProperty } from '@spazious/dollhouse-utils';
import { traverseUntil } from '@spazious/dollhouse-utils';
import { useEventCallback } from '@spazious/react-hooks';

import { useEvents } from '../components/EventManager/index.js';
import { DollhouseMessage, MainViewMessage } from '../core/EventManager.js';

const hasHiddenNodes = (node: FNode) => {
  let result = false;
  traverseUntil(node as FNode, (node) => {
    if (!((node as any).visible ?? true)) {
      result = true;
      return false;
    }
  });
  return result;
};

export const useNodeMetadata = () => {
  const events = useEvents();
  const { staging, createAction } = useStaging();
  const { select } = useSelection();

  const confirmReset = useEventCallback((onConfirmResponse: (message: MainViewMessage) => void) => {
    const cb = (message: MainViewMessage) => {
      events.off('mainViewMessage', cb);
      onConfirmResponse(message);
    };
    events.on('mainViewMessage', cb);
    events.emit('dollhouseMessage', DollhouseMessage.ConfirmInvisibleNodesReset);
  });

  const resetInvisiblenodes = useEventCallback((nodes: FNode[]) => {
    staging.prepare(true);
    const actions = nodes.flatMap((node) => {
      const nodeActions = [];
      if (hasOwnProperty(node, 'visible') && !(node as any).visible) {
        nodeActions.push(createAction('update', node.selector!, 'visible', true));
      }
      return nodeActions;
    });
    if (actions.length) {
      staging.add(actions);
      staging.commit(CommanderActionTypesEnum.Update);
      select([]);
    } else {
      staging.abort();
    }
  });

  const hasInvisiblenodes = useEventCallback((nodes: FNode[]) => {
    return nodes.some((node) => hasHiddenNodes(node));
  });

  const deletionsCount = useEventCallback((nodes: FNode[]) => {
    let count = 0;
    nodes.forEach((node: FNode) => {
      if (hasOwnProperty(node, 'visible') && !(node as any).visible) {
        count++;
      }
    });
    return count;
  });

  return {
    hasInvisiblenodes,
    confirmReset,
    resetInvisiblenodes,
    deletionsCount,
  };
};
